/**
 * Email validation utility — shared across all forms and popups.
 * Catches truncated domains, missing TLDs, and obviously invalid formats
 * BEFORE the form submits.
 */

const DOMAIN_FIXES = {
  'gmail': 'gmail.com',
  'yahoo': 'yahoo.com',
  'hotmail': 'hotmail.com',
  'outlook': 'outlook.com',
  'aol': 'aol.com',
  'icloud': 'icloud.com',
  'live': 'live.com',
  'msn': 'msn.com',
  'protonmail': 'protonmail.com',
  'sbcglobal': 'sbcglobal.net',
  'att': 'att.net',
  'comcast': 'comcast.net',
  'verizon': 'verizon.net',
  'cox': 'cox.net',
  'charter': 'charter.net',
};

/**
 * Normalize an email address — fixes truncated domains like user@gmail → user@gmail.com
 * @param {string} email
 * @returns {string} normalized email
 */
export function normalizeEmail(email) {
  if (!email || !email.includes('@')) return email;
  const [local, domain] = email.trim().toLowerCase().split('@');
  if (!local || !domain) return email;
  const fixed = DOMAIN_FIXES[domain];
  return fixed ? `${local}@${fixed}` : `${local}@${domain}`;
}

/**
 * Validate email format — returns error message or null if valid.
 * @param {string} email
 * @returns {string|null} error message or null
 */
export function validateEmail(email) {
  if (!email || !email.trim()) return 'Email is required';
  const normalized = normalizeEmail(email.trim());
  if (!normalized.includes('@')) return 'Email must contain @';
  const [local, domain] = normalized.split('@');
  if (!local) return 'Email is missing the part before @';
  if (!domain) return 'Email is missing the domain after @';
  if (!domain.includes('.')) return `Email domain "${domain}" is missing .com (or similar)`;
  if (domain.split('.').pop().length < 2) return 'Email has an invalid domain extension';
  // Basic regex for format
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  if (!re.test(normalized)) return 'Email format is invalid';
  return null;
}
